var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useCallout, useOkapiKy } from "@folio/stripes/core";
import { useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import { fetchProductById, fetchProductsForRelation, } from "../../api/productService";
import { fetchProductNumbersByProductIds } from "../../api/productNumberService";
// One keystroke must not pull the backend default (1000) products plus their
// numbers into a select; a more specific query narrows the list instead.
var RELATION_SEARCH_LIMIT = 100;
// Waited out after the last keystroke (on top of the Selection's own short
// debounce), so typing a title sends one search instead of one per letter.
export var RELATION_SEARCH_DEBOUNCE_MS = 300;
// Shorter text cannot use the backend's trigram indexes: a two-letter search
// scans every product number and title name (seconds on production data) and
// matches too many products to pick from anyway.
var RELATION_SEARCH_MIN_QUERY_LENGTH = 3;
// Numbers only refine the option label (volumes of one multi-volume set share
// title and form), so a failed lookup must not hide the products themselves.
// A cancelled lookup is not a failure and is reported to nobody.
var loadProductNumbers = function (kyInstance, products, onError, signal) { return __awaiter(void 0, void 0, void 0, function () {
    var productIds, _a;
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0:
                productIds = products
                    .map(function (product) { return product.id; })
                    .filter(function (id) { return !!id; });
                if (productIds.length === 0) {
                    return [2 /*return*/, {}];
                }
                _b.label = 1;
            case 1:
                _b.trys.push([1, 3, , 4]);
                return [4 /*yield*/, fetchProductNumbersByProductIds(kyInstance, productIds, signal)];
            case 2: return [2 /*return*/, _b.sent()];
            case 3:
                _a = _b.sent();
                if (!(signal === null || signal === void 0 ? void 0 : signal.aborted)) {
                    onError();
                }
                return [2 /*return*/, {}];
            case 4: return [2 /*return*/];
        }
    });
}); };
var reindexAfterRemoval = function (state, removedIndex) {
    var reindexed = {};
    Object.keys(state).forEach(function (key) {
        var keyNum = parseInt(key, 10);
        if (keyNum > removedIndex) {
            reindexed[keyNum - 1] = state[keyNum];
        }
        else if (keyNum < removedIndex) {
            reindexed[keyNum] = state[keyNum];
        }
    });
    return reindexed;
};
export var useProductRelations = function () {
    var _a = useState({}), productSearchResults = _a[0], setProductSearchResults = _a[1];
    var _b = useState({}), selectedRelatedProducts = _b[0], setSelectedRelatedProducts = _b[1];
    var _c = useState({}), productSearchLoading = _c[0], setProductSearchLoading = _c[1];
    // Rows whose filter text is too short to search for: they show a hint
    // instead of options.
    var _d = useState({}), productSearchTooShort = _d[0], setProductSearchTooShort = _d[1];
    var _e = useState({}), productNumbersById = _e[0], setProductNumbersById = _e[1];
    var mergeProductNumbers = function (numbers) {
        return setProductNumbersById(function (prev) { return (__assign(__assign({}, prev), numbers)); });
    };
    var kyInstance = useOkapiKy();
    var callout = useCallout();
    var formatMessage = useIntl().formatMessage;
    var reportNumbersLoadError = function () {
        return callout === null || callout === void 0 ? void 0 : callout.sendCallout({
            type: "error",
            message: formatMessage({
                id: "ui-br-agency.product.relatedProducts.loadError",
            }),
        });
    };
    var getValues = useFormContext().getValues;
    var initialRelations = getValues("productsRelations");
    var relatedProductIds = (initialRelations === null || initialRelations === void 0 ? void 0 : initialRelations.map(function (relation) { return relation.relatedProductId; }).filter(function (id) { return !!id; })) || [];
    useQuery(
    // "edit" keeps this apart from ProductDetail's ["relatedProducts", ids]
    // (a different data shape) while invalidating "relatedProducts" still
    // refreshes both.
    ["relatedProducts", "edit", relatedProductIds], function () { return __awaiter(void 0, void 0, void 0, function () {
        var products, numbers;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, Promise.all(relatedProductIds.map(function (id) { return fetchProductById(kyInstance, id); }))];
                case 1:
                    products = _a.sent();
                    return [4 /*yield*/, loadProductNumbers(kyInstance, products, reportNumbersLoadError)];
                case 2:
                    numbers = _a.sent();
                    return [2 /*return*/, { products: products, numbers: numbers }];
            }
        });
    }); }, {
        enabled: relatedProductIds.length > 0,
        onSuccess: function (_a) {
            var products = _a.products, numbers = _a.numbers;
            mergeProductNumbers(numbers);
            var productMap = {};
            initialRelations === null || initialRelations === void 0 ? void 0 : initialRelations.forEach(function (relation, index) {
                if (relation.relatedProductId) {
                    var product = products.find(function (p) { return p.id === relation.relatedProductId; });
                    if (product) {
                        productMap[index] = product;
                    }
                }
            });
            setSelectedRelatedProducts(productMap);
        },
    });
    var searchesByRow = useRef(new Map());
    var isCurrent = function (search) {
        return searchesByRow.current.get(search.index) === search;
    };
    var cancelSearch = function (search) {
        if (!search) {
            return;
        }
        clearTimeout(search.timer);
        search.controller.abort();
        search.done();
    };
    useEffect(function () { return function () {
        var searches = searchesByRow.current;
        searches.forEach(cancelSearch);
        searches.clear();
    }; }, []);
    var runSearch = function (search, query) { return __awaiter(void 0, void 0, void 0, function () {
        var signal, result, products_1, numbers, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    signal = search.controller.signal;
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 4, 5, 6]);
                    return [4 /*yield*/, fetchProductsForRelation(kyInstance, query, RELATION_SEARCH_LIMIT, signal)];
                case 2:
                    result = _b.sent();
                    if (!isCurrent(search)) {
                        return [2 /*return*/];
                    }
                    products_1 = result.items || [];
                    return [4 /*yield*/, loadProductNumbers(kyInstance, products_1, reportNumbersLoadError, signal)];
                case 3:
                    numbers = _b.sent();
                    if (!isCurrent(search)) {
                        return [2 /*return*/];
                    }
                    mergeProductNumbers(numbers);
                    setProductSearchResults(function (prev) {
                        var _a;
                        return (__assign(__assign({}, prev), (_a = {}, _a[search.index] = products_1, _a)));
                    });
                    return [3 /*break*/, 6];
                case 4:
                    _a = _b.sent();
                    if (!isCurrent(search)) {
                        return [2 /*return*/];
                    }
                    callout === null || callout === void 0 ? void 0 : callout.sendCallout({
                        type: "error",
                        message: formatMessage({
                            id: "ui-br-agency.product.relatedProducts.searchError",
                        }),
                    });
                    setProductSearchResults(function (prev) {
                        var _a;
                        return (__assign(__assign({}, prev), (_a = {}, _a[search.index] = [], _a)));
                    });
                    return [3 /*break*/, 6];
                case 5:
                    if (isCurrent(search)) {
                        searchesByRow.current.delete(search.index);
                        setProductSearchLoading(function (prev) {
                            var _a;
                            return (__assign(__assign({}, prev), (_a = {}, _a[search.index] = false, _a)));
                        });
                    }
                    search.done();
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); };
    /**
     * Searches products for the relation row at {@code index}. Called on every
     * change of the row's filter text: the request goes out only once typing
     * pauses, and any earlier search of the row is cancelled, so results and
     * loading always belong to the latest query. Only the trimmed text counts
     * and is sent; below the minimum length nothing is searched and the row is
     * flagged as too short. Resolves once this search is finished or cancelled.
     */
    var searchProducts = function (index, rawQuery) {
        var _a;
        cancelSearch(searchesByRow.current.get(index));
        searchesByRow.current.delete(index);
        var query = (_a = rawQuery === null || rawQuery === void 0 ? void 0 : rawQuery.trim()) !== null && _a !== void 0 ? _a : "";
        var tooShort = query.length < RELATION_SEARCH_MIN_QUERY_LENGTH;
        setProductSearchTooShort(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[index] = tooShort && query.length > 0, _a)));
        });
        if (tooShort) {
            setProductSearchResults(function (prev) {
                var _a;
                return (__assign(__assign({}, prev), (_a = {}, _a[index] = [], _a)));
            });
            setProductSearchLoading(function (prev) {
                var _a;
                return (__assign(__assign({}, prev), (_a = {}, _a[index] = false, _a)));
            });
            return Promise.resolve();
        }
        setProductSearchLoading(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[index] = true, _a)));
        });
        return new Promise(function (resolve) {
            var search = {
                index: index,
                controller: new AbortController(),
                done: resolve,
            };
            search.timer = setTimeout(function () { return runSearch(search, query); }, RELATION_SEARCH_DEBOUNCE_MS);
            searchesByRow.current.set(index, search);
        });
    };
    var clearRelationAtIndex = function (index) {
        var searches = searchesByRow.current;
        cancelSearch(searches.get(index));
        var remaining = Array.from(searches.values()).filter(function (search) { return search.index !== index; });
        searches.clear();
        remaining.forEach(function (search) {
            if (search.index > index) {
                search.index -= 1;
            }
            searches.set(search.index, search);
        });
        setSelectedRelatedProducts(function (prev) { return reindexAfterRemoval(prev, index); });
        setProductSearchResults(function (prev) { return reindexAfterRemoval(prev, index); });
        setProductSearchLoading(function (prev) { return reindexAfterRemoval(prev, index); });
        setProductSearchTooShort(function (prev) { return reindexAfterRemoval(prev, index); });
    };
    return {
        productSearchResults: productSearchResults,
        selectedRelatedProducts: selectedRelatedProducts,
        productSearchLoading: productSearchLoading,
        productSearchTooShort: productSearchTooShort,
        productNumbersById: productNumbersById,
        setSelectedRelatedProducts: setSelectedRelatedProducts,
        searchProducts: searchProducts,
        clearRelationAtIndex: clearRelationAtIndex,
    };
};
